import { useEffect, useMemo, useRef, useState } from "react";
import { useTablesContext } from "../../contexts/tablesContextProvider";
import { CustomTableProps } from "../../data-model/customTableModel";
import { LoaderMessage } from "../loaderMessage/loaderMessage";
import NoData from "../noData/noData";
import { CellData } from "./table";
import TableActions from "./tableActions";
import TableWithPagination from "./tableWithPagination";
import { handleRowClickAction } from "./utils/handleRowClickAction";
import { transformRowData } from "./utils/rowDataUtils";
import { sortTableData, sortTableHeadersByOrder } from "./utils/sorting";
import updateTableDataToRender from "./utils/updateTableDataToRender";

/**
 * Medonc-parity custom table for clinical-trials-dashboard.
 *
 * Built-in: search, filter (local context), sort (UI / server / disabled),
 * pagination, toolbar actions, loading/empty states. Cell rendering is limited
 * to {@link TableCellType.STRING}.
 *
 * @param props - {@link CustomTableProps}
 * @returns Table shell with actions and paginated body
 */
function RisaCustomTable(props: CustomTableProps) {
  const [computedCellData, setComputedCellData] = useState<CellData[][]>([]);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [endIndex, setEndIndex] = useState<number>(
    props?.itemsPerPage ? props.itemsPerPage - 1 : 0,
  );
  const [tableDataToRender, setTableDataToRender] = useState<any[]>([]);
  const [tableWidth, setTableWidth] = useState(0);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [numberOfTimesSearchPerformed, setNumberOfTimesSearchPerformed] =
    useState<number>(0);
  const [hasInitialRenderPassed, setHasInitialRenderPassed] = useState(false);
  useEffect(() => {
    setHasInitialRenderPassed(true);
  }, []);
  const tableRef = useRef<HTMLDivElement>(null);
  const defaultValueRef = useRef<string | null>(null);
  const { getTableDataFromContext: getTableData, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableData(props?.tableName);

  useEffect(() => {
    const dataToRender = updateTableDataToRender(
      searchResults,
      tableAttributes,
      props?.itemsPerPage ?? 0,
      handlePageChange,
      props.shouldDisableUiSorting ?? false,
    );

    setTableDataToRender(dataToRender);
  }, [JSON.stringify(searchResults), JSON.stringify(tableAttributes)]);

  useEffect(() => {
    if (props.tableData) {
      setSearchResults(props.tableData as any[]);
    }
  }, [JSON.stringify(props.tableData)]);

  useEffect(() => {
    if (!props.hidePagination && !props.serverSidePagination) return;
    if (tableDataToRender.length === 0) {
      setComputedCellData([]);
      return;
    }
    handlePageChange(0, tableDataToRender.length - 1, tableDataToRender);
  }, [
    props.hidePagination,
    props.serverSidePagination,
    JSON.stringify(tableDataToRender),
    JSON.stringify(props.tableHeaders),
  ]);

  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.offsetWidth);
    }
  }, [tableRef.current]);

  useEffect(() => {
    if (props.searchDefaultValue !== undefined) {
      defaultValueRef.current = props.searchDefaultValue;
      return;
    }
    const searchedTextFromContext = tableAttributes?.searchedText ?? null;
    defaultValueRef.current = searchedTextFromContext;
    props.searchingText?.(defaultValueRef.current ?? "");
  }, [props.tableName, props.searchDefaultValue, tableAttributes?.searchedText]);

  const onRowClick = (tableData: any, rowIndex: number, className: string) => {
    if (props.disableHoverAndClick) return;
    handleRowClickAction(tableData, rowIndex, className, startIndex, {
      onRowClick: props.onRowClick,
      toggleCheckboxSelection: props.toggleCheckboxSelection,
    });
  };

  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    const actualGlobalRowIndex = startIndex + rowIndex;

    setSearchResults((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[actualGlobalRowIndex]) {
        updatedData[actualGlobalRowIndex] = {
          ...updatedData[actualGlobalRowIndex],
          [fieldKey]: newValue,
        };
      }
      return updatedData;
    });

    setTableDataToRender((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[rowIndex]) {
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          [fieldKey]: newValue,
        };
      }
      return updatedData;
    });

    props.onTableRowUpdate?.(rowIndex, fieldKey, newValue);
  };

  function handlePageChange(start: number, end: number, data?: any[]) {
    const dataToUse = data && data.length > 0 ? data : tableDataToRender;

    if (!dataToUse || dataToUse.length === 0) return;

    const validStart = Math.max(0, start);
    const validEnd = Math.min(end, dataToUse.length - 1);

    setStartIndex(validStart);
    setEndIndex(validEnd);

    if (validEnd >= validStart) {
      props.endIndexOfTable && props.endIndexOfTable(validEnd);
    }

    props.startIndexOfTable && props.startIndexOfTable(validStart);

    setComputedCellData(
      dataToUse
        .slice(validStart, validEnd + 1)
        .map((rowData) => transformRowData(rowData, sortedTableHeaders)),
    );
  }

  /**
   * Handles a sort click on a column header.
   *
   * @param key - The matching header's `key`
   * @param subKey - The matching header's optional `subKey`
   * @param type - Sort comparator hint ("date" | "string" | "number")
   */
  const onSortTableData = (key: string, subKey?: string, type?: string) => {
    const isSameColumn =
      tableAttributes?.sortByKey === key &&
      tableAttributes?.sortBySubKey === subKey;
    const currentSortAscending = tableAttributes?.sortAscending ?? true;
    const shouldSortAscending = isSameColumn ? !currentSortAscending : true;

    const matchingHeader = sortedTableHeaders.find(
      (header) => header.key === key && header.subKey === subKey,
    );
    const sortField = matchingHeader?.sortField;

    setTableDataForContext(props.tableName, {
      sortByKey: key,
      sortBySubKey: subKey,
      sortByField: sortField,
      sortAscending: shouldSortAscending,
      sortType: type as "date" | "string" | "number" | undefined,
    });

    if (props.onSortTableClicked) {
      if (!sortField) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            `[RisaCustomTable] onSortTableClicked is provided but header for key="${key}"${
              subKey ? `, subKey="${subKey}"` : ""
            } is missing a "sortField". Skipping server-side sort emit.`,
          );
        }
        return;
      }

      props.onSortTableClicked(sortField, shouldSortAscending);
      return;
    }

    if (props.shouldDisableUiSorting) {
      return;
    }

    sortTableData(
      key,
      subKey,
      searchResults,
      shouldSortAscending,
      setTableDataToRender,
      startIndex,
      endIndex,
      type,
    );
  };

  const onSearchClicked = (searchText: string) => {
    setNumberOfTimesSearchPerformed((prev) => prev + 1);
    setTableDataForContext(props.tableName, {
      searchedText: searchText,
    });
    props?.searchingText && props.searchingText(searchText);
  };

  const sortedTableHeaders = sortTableHeadersByOrder(props.tableHeaders);
  const countToDisplay = props.totalCount ?? props.count;

  const paginationData = useMemo(() => {
    if (countToDisplay === 0) {
      return "Gathering metadata...";
    }
    if (props.serverSidePagination) {
      const currentPage = props.currentPage ?? 0;
      const pageSize = props.itemsPerPage ?? 0;
      const rangeStart = currentPage * pageSize + 1;
      const rangeEnd = Math.min(
        countToDisplay ?? 0,
        currentPage * pageSize + searchResults.length,
      );
      return `Showing ${rangeStart} - ${rangeEnd} of ${countToDisplay} items`;
    }
    return props?.isFilterApplied
      ? `Filtered ${props.count ?? 0} items out of ${countToDisplay} items`
      : `Showing ${startIndex + 1} - ${Math.min(endIndex + 1, searchResults.length)} of ${countToDisplay} items`;
  }, [
    countToDisplay,
    props.isFilterApplied,
    props.count,
    startIndex,
    endIndex,
    searchResults.length,
    props.serverSidePagination,
    props.currentPage,
    props.itemsPerPage,
  ]);

  return (
    <div className="table-container" ref={tableRef}>
      <>
        <TableActions
          onSearchClicked={onSearchClicked}
          onRefreshButton={props.onRefreshButton}
          tableName={props.tableName}
          defaultValueRef={defaultValueRef}
          showFilterButton={props.showFilterButton}
          hideSearchBar={props.hideSearchBar}
          filterSectionsData={props.filterSectionsData}
          onFilterApplyClicked={props.onFilterApplyClicked}
          showExportButton={props.showExportButton}
          onExportButtonClick={props.onExportButtonClick}
          showRefreshButton={props.showRefreshButton}
          onBulkAssignButtonClick={props.onBulkAssignButtonClick}
          showBulkAssignButton={props.showBulkAssignButton}
          bulkAssignButtonDisabled={props.bulkAssignButtonDisabled}
          bulkAssignSelectedCount={props.bulkAssignSelectedCount}
          isDisabled={props.isDisabled}
          tableWidth={tableWidth}
          onSortTableData={onSortTableData}
          sortedTableHeaders={sortedTableHeaders}
          onSelectAll={props.onSelectAll}
          tableDataLength={
            props.tableData?.length && props.isFetching
              ? 0
              : props.tableData?.length
          }
          exportingInProgress={props.exportingInProgress}
          placeholder={props.placeholder}
          searchDefaultValue={props.searchDefaultValue}
          showOtherButton={props.showOtherButton}
          otherButtonMeta={props.otherButtonMeta}
          isCheckboxSelected={props.isCheckboxSelected ?? false}
          screenName={props?.screenName}
          shouldLogEvent={props?.shouldLogEvent ?? false}
        />
      </>

      {props.isFetching || !hasInitialRenderPassed ? (
        <LoaderMessage message="Fetching Data ..." />
      ) : (
        <>
          {searchResults.length > 0 ? (
            <TableWithPagination
              tableName={props.tableName}
              sortedTableHeaders={sortedTableHeaders}
              computedCellData={computedCellData}
              tableDataToRender={tableDataToRender}
              tableWidth={tableWidth}
              onSortTableData={onSortTableData}
              onRowClick={onRowClick}
              hidePagination={props.hidePagination}
              count={
                props.serverSidePagination
                  ? (props.totalCount ?? props.count ?? undefined)
                  : (props.count ?? undefined)
              }
              itemsPerPage={props.itemsPerPage}
              pagesPerView={props.pagesPerView}
              handlePageChange={handlePageChange}
              triggerHandlePageChange={
                numberOfTimesSearchPerformed +
                (props.requestForPaginationReset ?? 0)
              }
              paginationData={paginationData}
              showInLineLoader={props.showInLineLoader}
              onTableRowUpdate={handleTableRowUpdate}
              startIndex={startIndex}
              isPreviousButtonDisabled={props.isPreviousButtonDisabled}
              currentPage={props.currentPage}
              serverSidePagination={props.serverSidePagination}
              onPageChange={props.onPageChange}
              rowClassName={props.rowClassName}
              onClickableBadgeClick={props.onClickableBadgeClick}
            />
          ) : (
            <NoData text={props.noDataText} />
          )}
        </>
      )}
    </div>
  );
}

export default RisaCustomTable;
export { RisaCustomTable };
